import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { api } from '@/shared/api/http';
import { useCurrentUserOrNull } from '@/shared/auth/hooks';
import type { SearchResult } from '@/shared/registry/types';

import { effectiveTimeZone, NO_RANGE, resolveRange, type SearchPeriod, type SearchRange } from './period';

/*
 * Search API (`/api/search`, docs/modules/search.md) and its TanStack Query hooks; keys start with 'search'.
 */

/**
 * A hit. `titleHtml` and `snippet` are HTML-encoded text with the matches in `<mark>` (render them with
 * `Highlighted`, never as raw HTML). `updatedAt` is when the entity last changed (what the period filter reads),
 * `authorId` who wrote it (what the author filter reads; null for documents without an author).
 */
export interface SearchHit extends SearchResult {
  titleHtml: string;
  updatedAt: string;
  authorId?: string | null;
}

export interface SearchFacet {
  module: string;
  count: number;
}

export interface SearchResponse {
  query: string;
  items: SearchHit[];
  page: number;
  pageSize: number;
  total: number;
  facets: SearchFacet[];
}

/** The narrowing filters of a search: who wrote it and when it changed (instants). */
export interface SearchFilters extends SearchRange {
  author: string | null;
}

export const NO_FILTERS: SearchFilters = { author: null, ...NO_RANGE };

export interface SearchParams {
  q: string;
  module: string | null;
  page: number;
  filters?: SearchFilters;
}

/** Shortest query the server searches. */
export const MIN_QUERY_LENGTH = 2;
export const PAGE_SIZE = 20;
/** Hits fetched for the ⌘K palette (grouped by module there). */
export const PREVIEW_SIZE = 18;

export const searchKeys = {
  all: ['search'] as const,
  results: (params: SearchParams) => ['search', 'results', params] as const,
  preview: (q: string, exclude: readonly string[], options: PreviewOptions) =>
    ['search', 'preview', q, exclude, options] as const,
};

const searchable = (q: string) => q.trim().length >= MIN_QUERY_LENGTH;

/**
 * The instants of a period in the signed-in user's time zone. The range is computed when the period (or the zone)
 * changes, not on every render, so a request keeps its key while the page is open.
 */
export function useSearchRange(period: SearchPeriod): SearchRange {
  const zone = effectiveTimeZone(useCurrentUserOrNull()?.timeZone);
  const { period: selection, from, to } = period;
  return useMemo(
    () => resolveRange({ period: selection, from, to }, new Date(), zone),
    [selection, from, to, zone],
  );
}

export function useSearchResults(params: SearchParams) {
  const filters = params.filters ?? NO_FILTERS;
  return useQuery({
    queryKey: searchKeys.results({ ...params, filters }),
    queryFn: ({ signal }) =>
      api.get<SearchResponse>('/search', {
        query: {
          q: params.q.trim(),
          module: params.module,
          author: filters.author,
          from: filters.from,
          to: filters.to,
          page: params.page,
          pageSize: PAGE_SIZE,
        },
        signal,
      }),
    enabled: searchable(params.q),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** What narrows the palette's live results (the palette's filter row). */
export interface PreviewOptions extends Partial<SearchFilters> {
  module?: string | null;
  /** Hits to fetch (default {@link PREVIEW_SIZE}). */
  pageSize?: number;
}

/**
 * Live results for the palette, without the modules in `exclude` (`usePaletteExclusions`: employees, whom the palette
 * lists from the directory, and modules that list their own entities there).
 */
export function useSearchPreview(
  q: string,
  exclude: readonly string[],
  enabled = true,
  options: PreviewOptions = {},
) {
  const query = q.trim();
  return useQuery({
    queryKey: searchKeys.preview(query, exclude, options),
    queryFn: ({ signal }) =>
      api.get<SearchResponse>('/search', {
        query: {
          q: query,
          module: options.module,
          author: options.author,
          from: options.from,
          to: options.to,
          pageSize: options.pageSize ?? PREVIEW_SIZE,
          exclude,
        },
        signal,
      }),
    enabled: enabled && searchable(query),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}
